import Split50_50Icon from "@fla/components/src/assets/svgs/50_50.svg";
import DollarIcon from "@fla/components/src/assets/svgs/dollar.svg";
import DollarLockIcon from "@fla/components/src/assets/svgs/dollar_lock.svg";
import PercentageIcon from "@fla/components/src/assets/svgs/percentage.svg";
import SellIcon from "@fla/components/src/assets/svgs/sell.svg";
import UsersIcon from "@fla/components/src/assets/svgs/users.svg";

import { AssetType, InitialDetails } from "../../../interfaces";

export function useOptions({ initialDetails }: { initialDetails: InitialDetails }) {
  const ownerV1Options = [
    {
      label: `${initialDetails?.user?.first_name} (You)`,
      value: "user"
    },
    { label: `${initialDetails?.spouse?.first_name}`, value: "spouse" }
  ];

  const ownerV2Options = [
    {
      label: `${initialDetails?.user?.first_name} (You)`,
      value: "user"
    },
    { label: `${initialDetails?.spouse?.first_name}`, value: "spouse" },
    { label: "Jointly Owned", value: "joint" }
  ];

  const ownerV3Options = [
    { label: `${initialDetails?.user?.first_name} (You)`, value: "user" },
    { label: `${initialDetails?.spouse?.first_name}`, value: "spouse" },
    { label: "Jointly Owned", value: "joint" },
    {
      label: "Something Else",
      value: "something_else"
    }
  ];

  // this used to determine primary care giver.
  const primaryCareGiverList = [
    { label: `${initialDetails?.user?.first_name} (You)`, value: "user" },
    { label: `${initialDetails?.spouse?.first_name}`, value: "spouse" },
    { label: "Equally (50/50)", value: "joint" },
    { label: "Other", value: "other" }
  ];

  const binaryWithOtherList = [
    { label: `${initialDetails?.user?.first_name} (You)`, value: "user" },
    { label: `${initialDetails?.spouse?.first_name}`, value: "spouse" },
    { label: "Other", value: "other" }
  ];

  const entityOptions = [
    { label: "Company", value: "company" },
    { label: "Sole Trader", value: "sole_trader" },
    { label: "Partnership", value: "partnership" },
    { label: "Trust", value: "trust" }
  ];

  const saleProceedsOptions = [
    {
      label: {
        img: UsersIcon,
        title: `${initialDetails?.user?.first_name} Keeps It`
      },
      value: "user"
    },
    {
      label: {
        img: UsersIcon,
        title: `${initialDetails?.spouse?.first_name} Keeps It`
      },
      value: "spouse"
    },
    {
      label: {
        img: Split50_50Icon,
        title: "Split 50/50"
      },
      value: "50_50"
    },
    {
      label: {
        img: PercentageIcon,
        title: "Divide by Percentage"
      },
      value: "percentage"
    }
  ];

  const splitByOptions = [
    {
      label: {
        img: UsersIcon,
        title: `${initialDetails?.user?.first_name} Keeps It`
      },
      value: "user"
    },
    {
      label: {
        img: UsersIcon,
        title: `${initialDetails?.spouse?.first_name} Keeps It`
      },
      value: "spouse"
    },
    {
      label: {
        img: SellIcon,
        title: "Sell It"
      },
      value: "sell"
    },

    {
      label: {
        img: PercentageIcon,
        title: "Divide by Percentage"
      },
      value: "percentage"
    }
  ];

  const debtSplitByOptions = [
    {
      label: {
        img: UsersIcon,
        title: `${initialDetails?.user?.first_name} Responsible for It`
      },
      value: "user"
    },
    {
      label: {
        img: UsersIcon,
        title: `${initialDetails?.spouse?.first_name} Responsible for It`
      },
      value: "spouse"
    },
    {
      label: {
        img: PercentageIcon,
        title: "Divide by Percentage"
      },
      value: "percentage"
    }
  ];

  const assetTypeOptions = [
    {
      label: "Real Estate",
      value: AssetType.REAL_ESTATE
    },
    {
      label: "Bank Account",
      value: AssetType.BANK
    },
    {
      label: "Investment",
      value: AssetType.INVESTMENTS
    },
    {
      label: "Retirement Fund",
      value: AssetType.RETIREMENT_FUND
    },
    {
      label: "Motor Vehicle",
      value: AssetType.VEHICLE
    },
    {
      label: "Business",
      value: AssetType.BUSINESS
    },
    {
      label: "Trust",
      value: AssetType.TRUST
    },
    {
      label: "Other",
      value: AssetType.OTHER
    }
  ];

  //--------------------------------- bank -------------------------------
  const bankSplitByOptions = [
    {
      label: {
        img: UsersIcon,
        title: `${initialDetails?.user?.first_name} Keeps It`
      },
      value: "user"
    },
    {
      label: {
        img: UsersIcon,
        title: `${initialDetails?.spouse?.first_name} Keeps It`
      },
      value: "spouse"
    },
    {
      label: {
        img: Split50_50Icon,
        title: "Split 50/50"
      },
      value: "50_50"
    },
    {
      label: {
        img: PercentageIcon,
        title: "Divide by Percentage"
      },
      value: "percentage"
    }
  ];

  // -------------------------- real estate --------------------------
  const realEstatePaymentMethodOptions = [
    {
      label: {
        img: DollarLockIcon,
        title: "Fixed Amount",
        desc: `Eg a figure like $20,000.`
      },
      value: "fixed_amount"
    },
    {
      label: {
        img: PercentageIcon,
        title: "Percentage of Equity",
        desc: `You divide the funds in a specified percentage.`
      },
      value: "percentage"
    },
    {
      label: {
        img: DollarIcon,
        title: "Something else",
        desc: `Use this option if you do not fit into one of the three options.`
      },
      value: "something_else"
    }
  ];

  const realEstatePropertyStatusOptions = [
    {
      label: {
        img: SellIcon,
        title: "Sell it",
        desc: `Sell to an independent buyer and divide sale the proceeds.`
      },
      value: "sell"
    },
    {
      label: {
        img: UsersIcon,
        title: "One of us keeps it",
        desc: `Refinance property and any mortgage to one of your names.`
      },
      value: "one_keeps"
    }
  ];

  const realEstateSaleProceedsOptions = [
    {
      label: {
        img: Split50_50Icon,
        title: "Split 50/50",
        desc: `Divide it equally between you both.`
      },
      value: "50_50"
    },
    {
      label: {
        img: UsersIcon,
        title: "One of us keeps it all",
        desc: `Either you or ${initialDetails?.spouse?.first_name} keep it all.`
      },
      value: "one_keeps_all"
    },
    {
      label: {
        img: PercentageIcon,
        title: "Split by a percentage",
        desc: `You divide the funds in a specified percentage.`
      },
      value: "split_by_percentage"
    },
    {
      label: {
        img: DollarIcon,
        title: "Something else",
        desc: `Use this option if you do not fit into one of the three options.`
      },
      value: "something_else"
    }
  ];

  const realEstateSaleProceedsOwnerOptions = [
    { label: `You (${initialDetails?.user?.first_name})`, value: "user" },
    { label: `${initialDetails?.spouse?.first_name}`, value: "spouse" }
  ];

  const realEstateTransferCostsOptions = [
    { label: `You (${initialDetails?.user?.first_name})`, value: "user" },
    { label: `${initialDetails?.spouse?.first_name}`, value: "spouse" },
    { label: "Split Equally", value: "joint" }
  ];

  const furnitureCategoryOptions = [
    {
      label:
        "We don’t live together. We’ve have already divided our furniture & personal effects. We each keep what is in our respective residence.",
      value: "category_1"
    },
    {
      label:
        "We still have some jointly owned furniture & personal effects which needs to be divided, or we don’t fall into category 1.",
      value: "category_2"
    }
  ];

  return {
    debtSplitByOptions,
    entityOptions,
    saleProceedsOptions,
    splitByOptions,
    assetTypeOptions,
    // real estate options
    realEstatePaymentMethodOptions,
    realEstatePropertyStatusOptions,
    realEstateSaleProceedsOptions,
    realEstateSaleProceedsOwnerOptions,
    realEstateTransferCostsOptions,
    // bank options
    bankSplitByOptions,

    // owner options
    ownerV1Options,
    ownerV2Options,
    ownerV3Options,

    // furniture
    furnitureCategoryOptions,

    primaryCareGiverList,
    binaryWithOtherList
  };
}
